import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import { PhoneInput } from '@/components/PhoneInput';
import { SelectInput } from '@/components/SelectInput';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { useRole } from '@/hooks/UseRole';
import { useCompanyStore } from '@/stores/useCompanyStore';
import { Company } from '@/types/company';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

export const UpdateCompanyGeneralInformations = () => {
  const { t } = useTranslation();
  const { updateCompany, loading, company } = useCompanyStore();
  const isAdmin = useRole('admin');

  const validationSchema = yup.object({
    name: yup.string().required(t('common:validation.name.required')),
    address: yup.string().nullable(),
    vat_number: yup.string().nullable(),
    phone: yup.string().nullable(),
    email: yup.string().email(t('common:validation.email.format')).nullable(),
    website: yup.string().nullable(),
    status: yup.string().required(t('common:validation.status.required')),
    logo: yup.mixed().nullable(),
  });

  const handleUpdateCompany = async (
    values: Company,
    { setErrors, setSubmitting }: FormikHelpers<Company>,
  ) => {
    try {
      setSubmitting(true);
      await updateCompany(values);
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  if (!company) return <Loading />;

  return (
    <div className="flex flex-col items-center justify-center md:items-start">
      <Text className="text-base font-medium text-sky-800">
        {t('updateCompany:general.information.title')}
      </Text>
      <Formik
        enableReinitialize
        initialValues={company}
        validationSchema={validationSchema}
        onSubmit={handleUpdateCompany}
      >
        {({ errors, touched, handleChange, values }) => (
          <Form className="mt-4 grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            <TextInput
              label={t('updateCompany:general.information.label.name')}
              name="name"
              type="text"
              placeholder={t(
                'updateCompany:general.information.placeholder.name',
              )}
              error={!!touched.name && !!errors.name}
              isRequired
            />
            <TextInput
              label={t('updateCompany:general.information.label.address')}
              name="address"
              type="text"
              placeholder={t(
                'updateCompany:general.information.placeholder.address',
              )}
              error={!!touched.address && !!errors.address}
            />
            <TextInput
              label={t('updateCompany:general.information.label.vat-number')}
              name="vat_number"
              type="text"
              placeholder={t(
                'updateCompany:general.information.placeholder.vat-number',
              )}
              error={!!touched.vat_number && !!errors.vat_number}
            />
            <PhoneInput
              label={t('updateCompany:general.information.label.phone')}
              name="phone"
              placeholder={t(
                'updateCompany:general.information.placeholder.phone',
              )}
              error={!!touched.phone && !!errors.phone}
              isRequired
            />
            <TextInput
              label={t('updateCompany:general.information.label.email')}
              name="email"
              type="email"
              placeholder={t(
                'updateCompany:general.information.placeholder.email',
              )}
              error={!!touched.email && !!errors.email}
            />
            <TextInput
              label={t('updateCompany:general.information.label.website')}
              name="website"
              type="text"
              placeholder={t(
                'updateCompany:general.information.placeholder.website',
              )}
              error={!!touched.website && !!errors.website}
            />
            {isAdmin && (
              <SelectInput
                label={t('updateCompany:general.information.label.status')}
                name="status"
                options={[
                  {
                    value: 'active',
                    label: t('updateCompany:general.information.status.active'),
                  },
                  {
                    value: 'inactive',
                    label: t(
                      'updateCompany:general.information.status.inactive',
                    ),
                  },
                ]}
                defaultValue={values.status}
                onChange={handleChange}
                error={errors.status as string}
                isRequired
              />
            )}
            <div className="col-span-1 mt-4 w-full md:col-span-2">
              <Button
                className="w-full lg:w-auto"
                type="submit"
                disabled={loading}
              >
                {t('updateCompany:general.information.update')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
